/**
 * Rejection Modal
 *
 * Formalizes the rejection process with mandatory administrative remarks.
 * Follows Operational Architect precision and clarity.
 */
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@lms/ui';
import { useState } from 'react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface RejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
  participantName: string;
}

export function RejectionModal({
  isOpen,
  onClose,
  onConfirm,
  isPending,
  participantName,
}: RejectionModalProps) {
  const [reason, setReason] = useState('');
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface w-full max-w-md rounded-2xl shadow-level3 overflow-hidden border border-outline-variant/10 animate-scaleIn my-auto">
        {/* Header */}
        <div className="px-6 py-4 bg-error-container/10 border-b border-error/10 flex justify-between items-center">
          <div className="flex items-center gap-3 text-error">
            <AlertTriangle size={18} />
            <span className="text-xs font-label font-bold uppercase tracking-widest">
              <Bilingual {...copy.rejectionModal.protocolLabel} />
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          <div>
            <h3 className="text-xl font-headline font-bold text-on-surface m-0 mb-1">
              <Bilingual {...copy.rejectionModal.title} />
            </h3>
            <p className="text-sm font-body text-on-surface-variant">
              {t(copy.rejectionModal.desc).replace('{name}', participantName)}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
              <Bilingual {...copy.rejectionModal.labelRemarks} />
            </label>
            <textarea
              className="w-full bg-surface-container-high border border-outline-variant/20 rounded-xl p-4 text-sm font-body outline-none focus:ring-2 focus:ring-error/20 resize-none h-32 transition-all"
              placeholder={t(copy.rejectionModal.placeholder)}
              value={reason}
              onChange={e => setReason(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-8 py-6 bg-surface-container-lowest border-t border-outline-variant/10 flex gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            className="flex-1 rounded-xl text-xs uppercase tracking-widest font-bold"
          >
            <Bilingual {...copy.rejectionModal.cancel} />
          </Button>
          <Button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || isPending}
            className="flex-1 rounded-xl bg-error text-white hover:bg-error/90 text-xs uppercase tracking-widest font-bold shadow-level1"
          >
            {isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Bilingual {...copy.rejectionModal.confirm} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
